"use client";

import { c as _c } from "react/compiler-runtime";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
import { createInitialMemesQuickVoteDiscoveryState, deriveMemesQuickVoteDiscoverySnapshot, getMemesQuickVoteActiveCandidateId, getMemesQuickVoteNextCandidateId } from "@/hooks/memesQuickVote.queue.helpers";
import { fetchMemesQuickVoteDiscoveryBatch, fetchMemesQuickVoteDrop, fetchMemesQuickVoteSummary, getMemesQuickVoteDiscoveryQueryKey, getMemesQuickVoteDiscoveryStateKey, getMemesQuickVoteDropQueryKey, getMemesQuickVoteSummaryQueryKey } from "@/hooks/memesQuickVote.query";
import { useMemesQuickVoteContext } from "@/hooks/useMemesQuickVoteContext";
import { useMemesQuickVoteStorage } from "@/hooks/useMemesQuickVoteStorage";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
export const usePrefetchMemesQuickVote = () => {
  const $ = _c(11);
  const queryClient = useQueryClient();
  const {
    contextProfile,
    isEnabled,
    memesWaveId,
    proxyId
  } = useMemesQuickVoteContext();
  let t0;
  if ($[0] !== contextProfile || $[1] !== memesWaveId || $[2] !== proxyId) {
    t0 = {
      contextProfile,
      memesWaveId,
      proxyId
    };
    $[0] = contextProfile;
    $[1] = memesWaveId;
    $[2] = proxyId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const {
    skippedDropIds
  } = useMemesQuickVoteStorage(t0);
  let t1;
  if ($[4] !== contextProfile || $[5] !== isEnabled || $[6] !== memesWaveId || $[7] !== proxyId || $[8] !== queryClient || $[9] !== skippedDropIds) {
    t1 = async sessionId => {
      const waveId = typeof memesWaveId === "string" && memesWaveId.length > 0 ? memesWaveId : null;
      if (!isEnabled || !contextProfile || !waveId) {
        return;
      }
      const discoveryState = createInitialMemesQuickVoteDiscoveryState();
      const discoveryStateKey = getMemesQuickVoteDiscoveryStateKey({
        contextProfile,
        enabled: true,
        memesWaveId: waveId,
        sessionId
      });
      const summaryPromise = queryClient.prefetchQuery({
        queryKey: getMemesQuickVoteSummaryQueryKey({
          contextProfile,
          proxyId,
          waveId
        }),
        queryFn: () => fetchMemesQuickVoteSummary(waveId),
        staleTime: 60000,
        ...getDefaultQueryRetry()
      });
      const discoveryData = await queryClient.fetchQuery({
        queryKey: getMemesQuickVoteDiscoveryQueryKey({
          discoveryStateKey,
          fetchVersion: 0,
          waveId
        }),
        queryFn: () => fetchMemesQuickVoteDiscoveryBatch({
          discoveryState,
          skippedDropIds,
          waveId
        }),
        retry: false
      });
      const discoverySnapshot = deriveMemesQuickVoteDiscoverySnapshot({
        enabled: true,
        pages: discoveryData.pages,
        skippedDropIds,
        state: discoveryState
      });
      const activeCandidateId = getMemesQuickVoteActiveCandidateId(discoverySnapshot);
      const nextCandidateId = getMemesQuickVoteNextCandidateId(discoverySnapshot);
      const dropPrefetches = [activeCandidateId, nextCandidateId].filter(_temp).map(dropId_0 => queryClient.prefetchQuery({
        queryKey: getMemesQuickVoteDropQueryKey({
          contextProfile,
          dropId: dropId_0,
          proxyId
        }),
        queryFn: () => fetchMemesQuickVoteDrop(dropId_0),
        ...getDefaultQueryRetry()
      }));
      await Promise.all([summaryPromise, ...dropPrefetches]);
    };
    $[4] = contextProfile;
    $[5] = isEnabled;
    $[6] = memesWaveId;
    $[7] = proxyId;
    $[8] = queryClient;
    $[9] = skippedDropIds;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
};
function _temp(dropId) {
  return !!dropId;
}